/**
 * The fixture-override layer for /demo (fnd-48a).
 *
 * `demo/fixtures/*` are static — one Republic Plumbing / Sarah Mitchell
 * account, imported once. `?from=<invite token>` needs the same fixtures to
 * read back the business, trade, city and contact from *that* invite
 * instead. Rather than fork the fixtures, this module holds one mutable
 * "current identity" that `demoApi.ts` reads at request time, and a tiny
 * pub/sub so React can re-render when it changes.
 *
 * Two paths set it, both driven by `DemoProvider`:
 *  - a demo token (`isDemoToken`) resolves from `DEMO_INVITES` synchronously,
 *    during render — no network, so it must be ready before the adapter
 *    answers the first request.
 *  - a real token resolves from `GET /api/invite/:token` asynchronously; the
 *    fixtures keep showing the default identity until that resolves (or
 *    fails, in which case they keep showing it).
 */

import axios from 'axios';
import type { SiteIdentity } from '@foundlio/pack';
import type { InviteInfo } from '../components/invite/kit';
import { personalFirstName } from '../lib/contactName';
import {
  DEMO_BUSINESS_NAME,
  DEMO_CONTACT_NAME,
  DEMO_DOMAIN,
  DEMO_PHONE,
  demoIdentity,
  demoPrintKit,
  demoUser,
  demoWebsite,
} from './fixtures/site';
import type { KitManifest, PrintKitState } from '../services/printKitService';
import { demoLeads } from './fixtures/leads';
import { demoReviews, type DemoReview } from './fixtures/reviews';
import { demoBookings } from './fixtures/bookings';
import { demoNotifications } from './fixtures/notifications';
import { demoMessageThread } from './fixtures/messages';
import { demoContentItems } from './fixtures/contentPlan';
import { jobsForTrade, normalizeTradeKey } from './tradeJobs';
import { REVIEW_COPY, BOOKING_COPY, NOTIFICATION_COPY, MESSAGE_COPY, CONTENT_COPY } from './tradeCopy';
import type { Lead, Booking, Notification } from '../types';
import type { Website } from '../types';
import type { MessageThread } from '../services/messageService';
import type { ContentItem } from '../services/contentPlanService';

export interface DemoIdentity {
  businessName: string;
  /** Raw trade string as the invite carries it (e.g. "roofing"). */
  trade: string;
  city: string | null;
  domain: string;
  previewUrl: string;
  screenshotUrl: string | null;
  /** Raw contact name, before `personalFirstName` — kept for debugging/tests. */
  contactName: string | null;
  /** What the greeting actually says: a first name, or 'Demo Account'. */
  displayName: string;
  /** The business's own phone, when the invite carried one. */
  phone: string | null;
  /**
   * The deployed site's own print-kit identity — `GET
   * /api/invite/:token/identity` (real logo, theme colours/fonts), fetched
   * best-effort alongside the invite lookup. `null` for demo tokens (no
   * bundle behind them) and whenever that fetch hasn't landed or failed —
   * `buildDemoPrintKit` synthesizes an identity from the fields above
   * instead, with `business.logo: null` so the pack pieces fall back to
   * their text-logo path rather than a broken `<img>`.
   */
  siteIdentity: SiteIdentity | null;
}

export const DEFAULT_DEMO_IDENTITY: DemoIdentity = {
  businessName: DEMO_BUSINESS_NAME,
  trade: 'plumbing',
  city: 'Madison',
  domain: DEMO_DOMAIN,
  previewUrl: `https://${DEMO_DOMAIN}`,
  screenshotUrl: demoWebsite.screenshot ?? null,
  contactName: DEMO_CONTACT_NAME,
  displayName: personalFirstName(DEMO_CONTACT_NAME, DEMO_BUSINESS_NAME) || 'Demo Account',
  phone: DEMO_PHONE,
  siteIdentity: null,
};

let current: DemoIdentity = DEFAULT_DEMO_IDENTITY;
const listeners = new Set<() => void>();

/**
 * The invite token the tour was opened from. Dashboard chrome links are
 * rebased onto /demo/… without the `?from=` query string, so the token has
 * to survive in memory: it keeps the resolved identity from being reset on
 * the next route and gives the top bar its way back to the invite.
 */
let sourceToken: string | null = null;

export function getDemoSource(): string | null {
  return sourceToken;
}

/**
 * Readiness gate for the fixture adapter. A real invite token resolves over
 * the network, and the dashboard's first queries fire in the same tick the
 * tour mounts — invalidating them after the fact does not reliably replace
 * an in-flight response. So the adapter awaits this promise before answering
 * anything: with a demo token or no token it is already settled; with a
 * real token it settles when the invite lookup finishes (or fails, in which
 * case the default identity answers).
 */
let ready: Promise<void> = Promise.resolve();
const inflight = new Map<string, Promise<DemoIdentity | null>>();

export function whenDemoIdentityReady(): Promise<void> {
  return ready;
}

/**
 * Resolve a real token and hold the adapter until it lands. Idempotent per
 * token (StrictMode renders twice) — the same lookup promise is reused.
 */
export function resolveDemoIdentityFrom(token: string): Promise<DemoIdentity | null> {
  let lookup = inflight.get(token);
  if (!lookup) {
    lookup = fetchInviteIdentity(token).then(identity => {
      if (identity && sourceToken === token) setDemoIdentity(identity);
      return identity;
    });
    inflight.set(token, lookup);
  }
  ready = lookup.then(() => undefined);
  return lookup;
}

export function setDemoSource(token: string | null): void {
  sourceToken = token;
}

export function getDemoIdentity(): DemoIdentity {
  return current;
}

export function setDemoIdentity(identity: DemoIdentity): void {
  current = identity;
  listeners.forEach(listener => listener());
}

export function resetDemoIdentity(): void {
  sourceToken = null;
  setDemoIdentity(DEFAULT_DEMO_IDENTITY);
}

export function subscribeDemoIdentity(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `InviteInfo` (demo fixture or the real API response) → `DemoIdentity`. */
export function identityFromInvite(info: InviteInfo): DemoIdentity {
  const website = info.website;
  const businessName = website?.businessName || info.businessName || 'Your business';
  // Mirrors InvitePage: `inviteInfo.name` is the business name on every
  // pipeline-created invite, so it's only a fallback contact, and one
  // `personalFirstName` throws out unless it genuinely reads as a person.
  const rawContactName = website?.contactName || info.name || null;
  const displayName = personalFirstName(rawContactName || '', businessName) || 'Demo Account';
  const domain = website?.domain || DEFAULT_DEMO_IDENTITY.domain;

  return {
    businessName,
    trade: website?.trade || 'generic',
    city: website?.city || null,
    domain,
    previewUrl: website?.previewUrl || `https://${domain}`,
    screenshotUrl: website?.screenshotUrl ?? null,
    contactName: rawContactName,
    displayName,
    phone: website?.businessPhone ?? null,
    // `GET /api/invite/:token` (the only request the demo tour is allowed to
    // make for a real token) doesn't carry a logo — that lives behind
    // `/api/invite/:token/identity`, a second round-trip the tour skips on
    // purpose. `buildDemoPrintKit` synthesizes a logo-less `SiteIdentity`
    // from these fields instead, which the pack pieces render as a text logo
    // (never a broken `<img>`).
    siteIdentity: null,
  };
}

/**
 * `GET /api/invite/:token`, on an axios instance that shares nothing with
 * `services/api` or the bare `axios` default — both of those get their
 * `adapter` swapped by `installDemoApi()`, and this request is the one call
 * out of /demo that must actually reach the network. Failures resolve to
 * `null`; the caller keeps the current (default) identity.
 */
const inviteHttp = axios.create();

export async function fetchInviteIdentity(token: string): Promise<DemoIdentity | null> {
  try {
    const response = await inviteHttp.get<{ success: boolean; data: InviteInfo }>(
      `/api/invite/${encodeURIComponent(token)}`
    );
    return identityFromInvite(response.data.data);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Fixture builders — demoApi.ts reads these instead of the static     */
/* Republic Plumbing constants, so every request reflects the current  */
/* identity without the fixtures themselves needing to be mutable.     */
/* ------------------------------------------------------------------ */

export function buildDemoUser(identity: DemoIdentity = getDemoIdentity()) {
  return {
    ...demoUser,
    // Sidebar / settings identity — a real name when we have one, the
    // business name otherwise. Keep `name` this way (not the greeting
    // string) so it reads the same as a real account's would.
    name: identity.contactName || identity.businessName,
    // Consumed only by the DashboardHome greeting: a first name, or the
    // literal 'Demo Account' — resolved once here so the greeting never
    // re-runs `personalFirstName` on an already-final string.
    displayName: identity.displayName,
  };
}

export function buildDemoWebsite(identity: DemoIdentity = getDemoIdentity()): Website {
  return {
    ...demoWebsite,
    domain: identity.domain,
    currentActiveUrl: identity.previewUrl,
    netlifyUrl: `https://${identity.domain}`,
    screenshot: identity.screenshotUrl ?? undefined,
    businessName: identity.businessName,
    trade: identity.trade,
  };
}

/**
 * The fixture leads, with `service` reassigned to a job title for the
 * current trade — same names, messages, statuses and dates, so a lead that
 * reads as an emergency plumbing call still reads as an emergency call, just
 * about a different job. Wraps if a trade has fewer job titles than there
 * are leads (there are 8 leads; every trade map has at least 4 jobs).
 */
export function buildDemoLeads(identity: DemoIdentity = getDemoIdentity()): Lead[] {
  const jobs = jobsForTrade(identity.trade);
  return demoLeads.map((lead, index) => ({
    ...lead,
    service: jobs[index % jobs.length],
  }));
}

/**
 * The fixture reviews, with review/response text swapped for the current
 * trade (`tradeCopy.ts`) — same reviewer names, ratings, platforms and
 * dates, so a landscaping invite doesn't open the tour on a "sump pump"
 * review. Any spot in the text that named the business (only two of the
 * five originally did) is re-templated onto the current identity.
 */
export function buildDemoReviews(identity: DemoIdentity = getDemoIdentity()): DemoReview[] {
  const copy = REVIEW_COPY[normalizeTradeKey(identity.trade)];
  return demoReviews.map((review, index) => {
    const entry = copy[index];
    if (!entry) return review;
    return {
      ...review,
      reviewText: entry.reviewText(identity.businessName),
      response: entry.response ? entry.response(identity.businessName) : null,
    };
  });
}

/** The fixture bookings, with `serviceType`/`notes` swapped for the current trade. */
export function buildDemoBookings(identity: DemoIdentity = getDemoIdentity()): Booking[] {
  const copy = BOOKING_COPY[normalizeTradeKey(identity.trade)];
  return demoBookings.map((booking, index) => {
    const entry = copy[index];
    if (!entry) return booking;
    return { ...booking, serviceType: entry.serviceType, notes: entry.notes };
  });
}

/**
 * The fixture notification bell, with the two lead messages, the review
 * notice (business name templated) and the "site updated" line swapped for
 * the current trade. Titles keep the fixture customer names; the billing
 * notice (index 4) is trade-neutral already.
 */
export function buildDemoNotifications(identity: DemoIdentity = getDemoIdentity()): Notification[] {
  const copy = NOTIFICATION_COPY[normalizeTradeKey(identity.trade)];
  return demoNotifications.map((notification, index) => {
    if (index === 0) return { ...notification, message: copy.leadMsg1 };
    if (index === 1) return { ...notification, message: copy.leadMsg2 };
    if (index === 2) return { ...notification, message: `Elaine Brooks reviewed ${identity.businessName}.` };
    if (index === 3) return { ...notification, message: copy.systemMsg };
    return notification;
  });
}

/** The fixture message thread, with the customer/team exchange swapped for the current trade. */
export function buildDemoMessageThread(identity: DemoIdentity = getDemoIdentity()): MessageThread {
  const copy = MESSAGE_COPY[normalizeTradeKey(identity.trade)];
  return {
    ...demoMessageThread,
    messages: demoMessageThread.messages.map((message, index) => {
      if (index === 0) return { ...message, body: copy.customer };
      if (index === 1) return { ...message, body: copy.team };
      return message;
    }),
  };
}

/**
 * The fixture content plan, with title/content/keywords swapped for the
 * current trade — same types, statuses and dates. `action` only differs on
 * the faq entry; the gallery reminder's action text is trade-neutral.
 */
export function buildDemoContentItems(identity: DemoIdentity = getDemoIdentity()): ContentItem[] {
  const copy = CONTENT_COPY[normalizeTradeKey(identity.trade)];
  return demoContentItems.map((item, index) => {
    const entry = copy[index];
    if (!entry) return item;
    return {
      ...item,
      title: entry.title,
      content: entry.content,
      keywords: entry.keywords,
      action: entry.action ?? item.action,
    };
  });
}

/** `businessName` → a filename-safe slug ("Noble Roots Landscaping" → "noble-roots-landscaping"). */
function slugifyBusinessName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'foundlio';
}

/**
 * The print-kit's `identity` (and the pieces of the manifest that name the
 * business), following the current identity instead of always answering the
 * default Republic Plumbing fixture — fnd-gt9. `?from=<real invite token>`
 * has no `siteIdentity` fetched for it (see `identityFromInvite`'s note), so
 * this synthesizes one from the fields the fixture-override layer does carry
 * (name, trade → vertical, city, domain, phone), with `business.logo: null`.
 * `identityToPack` (and the pack pieces downstream of it) already render a
 * `null` logo as the business name in text — never a broken `<img>` — so no
 * extra fallback is needed here.
 *
 * The default demo identity keeps answering the hand-written
 * `demoIdentity`/`demoPrintKit` fixture verbatim (same crimson-bold/tech
 * theme, same manifest byte sizes) so nothing changes for the untouched
 * /demo tour.
 */
export function buildDemoPrintKit(identity: DemoIdentity = getDemoIdentity()): PrintKitState {
  if (identity === DEFAULT_DEMO_IDENTITY) return demoPrintKit;

  const slug = slugifyBusinessName(identity.businessName);
  const siteIdentity: SiteIdentity =
    identity.siteIdentity ??
    ({
      slug,
      domain: identity.domain,
      business: {
        name: identity.businessName,
        phone: identity.phone,
        city: identity.city,
        state: null,
        vertical: identity.trade,
        established: null,
        establishedIsEstimate: false,
        logo: null,
      },
      // No per-business theme travels through the fixture-override layer —
      // keep the demo fixture's own brand rather than inventing one.
      theme: demoIdentity.theme,
      owner: identity.contactName ?? undefined,
    } satisfies SiteIdentity);

  const manifest: KitManifest | null = demoPrintKit.manifest && {
    ...demoPrintKit.manifest,
    slug,
    trade: identity.trade,
    baseUrl: identity.previewUrl,
    pieces: demoPrintKit.manifest.pieces.map(piece => ({
      ...piece,
      file: `${slug}-${piece.id}.pdf`,
    })),
  };

  return { ...demoPrintKit, identity: siteIdentity, manifest };
}
